import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { Dialog, DialogPanel } from "@tremor/react";
import HarViewer from "../HarViewer/index.jsx";
import client from "../../api/client.js";

function CheckDetailSummary({ type, data: d, error }) {
  const { t } = useTranslation("monitor_detail");

  if (error && !d.http_status && !d.days_remaining && !d.ttfb_ms) {
    return <span className="truncate text-red-500">{error}</span>;
  }

  if (type === "http" || type === "api") {
    const kwFailed =
      d.keyword_found === false ||
      d.keyword_absent_found === true ||
      d.keyword_regex_matched === false;
    return (
      <span className="flex items-center gap-2">
        {d.http_status && (
          <span className="font-mono">{d.http_status}</span>
        )}
        {d.ttfb_ms != null && (
          <span className="text-gray-400">{t("check_summary.ttfb")} {d.ttfb_ms.toFixed(0)} ms</span>
        )}
        {kwFailed && <span className="text-red-500">{t("check_summary.keyword_fail")}</span>}
        {!kwFailed && (d.keyword_found === true || d.keyword_regex_matched === true) && (
          <span className="text-green-600">{t("check_summary.keyword_pass")}</span>
        )}
        {error && <span className="truncate text-red-500">{error}</span>}
      </span>
    );
  }

  if (type === "ssl") {
    return (
      <span className="flex items-center gap-2">
        {d.days_remaining != null && (
          <span className={d.days_remaining <= 7 ? "text-red-500" : d.days_remaining <= 14 ? "text-yellow-600" : ""}>
            {t("check_summary.days_remaining", { count: d.days_remaining })}
          </span>
        )}
        {d.subject_cn && <span className="font-mono truncate">{d.subject_cn}</span>}
        {d.is_self_signed && (
          <span className="rounded-full bg-yellow-100 px-1.5 py-0.5 text-yellow-700">{t("check_summary.self_signed")}</span>
        )}
        {error && !d.days_remaining && <span className="text-red-500 truncate">{error}</span>}
      </span>
    );
  }

  if (type === "domain") {
    return (
      <span className="flex items-center gap-2">
        {d.days_remaining != null && (
          <span className={d.days_remaining <= 7 ? "text-red-500" : d.days_remaining <= 30 ? "text-yellow-600" : ""}>
            {t("check_summary.days_remaining", { count: d.days_remaining })}
          </span>
        )}
        {d.registrar && <span className="truncate text-gray-400">{d.registrar}</span>}
        {error && !d.days_remaining && <span className="text-red-500 truncate">{error}</span>}
      </span>
    );
  }

  if (type === "dns") {
    return d.resolved_ips?.length > 0 ? (
      <span className="font-mono truncate">{d.resolved_ips.slice(0, 2).join(", ")}{d.resolved_ips.length > 2 ? " …" : ""}</span>
    ) : error ? (
      <span className="text-red-500 truncate">{error}</span>
    ) : <span>—</span>;
  }

  if (type === "ping") {
    return (
      <span className="flex items-center gap-2">
        {d.packet_loss != null && <span>{t("check_summary.packet_loss", { pct: d.packet_loss })}</span>}
        {d.avg_rtt_ms != null && <span className="text-gray-400">{t("check_summary.avg_rtt", { ms: d.avg_rtt_ms.toFixed(1) })}</span>}
        {error && <span className="text-red-500 truncate">{error}</span>}
      </span>
    );
  }

  if (type === "port") {
    return (
      <span>
        {d.state ? (
          <span className={d.state === "open" ? "text-green-600" : "text-red-500"}>{d.state}</span>
        ) : error ? (
          <span className="text-red-500 truncate">{error}</span>
        ) : "—"}
      </span>
    );
  }

  if (type === "playwright") {
    return <span className="text-teal-600">{t("check_summary.view_details_inline")}</span>;
  }

  if (type === "shopify") {
    const flow = d.flow ?? {};
    const failedFlow = Object.values(flow).find((item) => item?.status === "down");
    const topImpact = d.third_party_impact?.[0];
    return (
      <span className="flex items-center gap-2">
        {failedFlow ? (
          <span className="text-red-500 truncate">{failedFlow.message}</span>
        ) : (
          <span className="text-gray-500 truncate">{d.business_summary ?? t("check_summary.storefront_checked")}</span>
        )}
        {topImpact?.impact === "high" && (
          <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-amber-700">
            {t("check_summary.app_impact")}
          </span>
        )}
      </span>
    );
  }

  return error ? (
    <span className="text-red-500 truncate">{error}</span>
  ) : <span>—</span>;
}

function PlaywrightCheckDrawer({ check, onClose }) {
  const { t } = useTranslation("monitor_detail");
  const meta = check.data ?? check.metadata ?? {};
  const isTransaction = Array.isArray(meta.step_results) && meta.step_results.length > 0;
  const [har, setHar] = useState(null);
  const [harLoading, setHarLoading] = useState(false);
  const [harError, setHarError] = useState(null);
  const [tab, setTab] = useState(isTransaction ? "steps" : (meta.screenshot_url || meta.screenshot_key) ? "screenshot" : "har");

  useEffect(() => {
    if (!meta.har_url) return;
    setHarLoading(true);
    client
      .get(meta.har_url)
      .then((res) => {
        const data = typeof res.data === "string" ? JSON.parse(res.data) : res.data;
        setHar(data);
      })
      .catch(() => setHarError(t("check_drawer.error_har")))
      .finally(() => setHarLoading(false));
  }, [meta.har_url, t]);

  const tabs = [
    isTransaction                                && { id: "steps",      label: t("check_drawer.tab_steps", { count: meta.step_results.length }) },
    (meta.screenshot_url || meta.screenshot_key) && { id: "screenshot", label: t("check_drawer.tab_screenshot") },
    meta.har_url                                 && { id: "har",        label: t("check_drawer.tab_har") },
  ].filter(Boolean);

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-w-5xl flex-col rounded-none bg-white p-0 shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{t("check_drawer.title")}</h2>
            <p className="mt-0.5 text-xs text-gray-400">
              {new Date(check.time).toLocaleString()}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {(meta.status_code || meta.http_status || meta.title || meta.final_url
          || meta.days_remaining !== undefined || meta.host
          || meta.subject_cn || meta.issuer_cn
          || meta.ttfb_ms !== undefined) && (
          <div className="flex flex-wrap gap-6 border-b border-gray-100 px-6 py-3 shrink-0">
            {(meta.http_status || meta.status_code) && (
              <div>
                <p className="text-xs text-gray-400">{t("check_drawer.meta_http_status")}</p>
                <p className="font-mono text-sm text-gray-700">
                  {meta.http_status ?? meta.status_code}
                </p>
              </div>
            )}
            {meta.method && (
              <div>
                <p className="text-xs text-gray-400">{t("check_drawer.meta_method")}</p>
                <p className="font-mono text-sm text-gray-700">{meta.method}</p>
              </div>
            )}
            {meta.ttfb_ms !== undefined && meta.download_ms !== undefined && (
              <div className="w-full">
                <p className="text-xs text-gray-400 mb-1">{t("check_drawer.meta_timing")}</p>
                {(() => {
                  const total = meta.ttfb_ms + meta.download_ms;
                  const ttfbPct = total > 0 ? (meta.ttfb_ms / total) * 100 : 50;
                  const dlPct = 100 - ttfbPct;
                  return (
                    <div>
                      <div className="flex h-3 w-full max-w-xs overflow-hidden rounded-full bg-gray-100">
                        <div
                          className="bg-teal-600"
                          style={{ width: `${ttfbPct}%` }}
                          title={t("check_drawer.meta_ttfb", { ms: meta.ttfb_ms.toFixed(1) })}
                        />
                        <div
                          className="bg-teal-200"
                          style={{ width: `${dlPct}%` }}
                          title={t("check_drawer.meta_download", { ms: meta.download_ms.toFixed(1) })}
                        />
                      </div>
                      <div className="mt-1 flex gap-4 text-xs text-gray-500">
                        <span>
                          <span className="inline-block h-2 w-2 rounded-full bg-teal-600 mr-1 align-middle" />
                          {t("check_drawer.meta_ttfb", { ms: meta.ttfb_ms.toFixed(1) })}
                        </span>
                        <span>
                          <span className="inline-block h-2 w-2 rounded-full bg-teal-200 mr-1 align-middle" />
                          {t("check_drawer.meta_download", { ms: meta.download_ms.toFixed(1) })}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
            {meta.keyword_found !== undefined && (
              <div>
                <p className="text-xs text-gray-400">{t("check_drawer.meta_keyword")}</p>
                <p className={`text-sm font-medium ${meta.keyword_found ? "text-green-600" : "text-red-500"}`}>
                  {meta.keyword_found ? t("check_drawer.meta_keyword_found") : t("check_drawer.meta_keyword_not_found")}
                </p>
              </div>
            )}
            {meta.keyword_absent_found !== undefined && (
              <div>
                <p className="text-xs text-gray-400">{t("check_drawer.meta_forbidden_keyword")}</p>
                <p className={`text-sm font-medium ${meta.keyword_absent_found ? "text-red-500" : "text-green-600"}`}>
                  {meta.keyword_absent_found ? t("check_drawer.meta_forbidden_found") : t("check_drawer.meta_forbidden_absent")}
                </p>
              </div>
            )}
            {meta.keyword_regex_matched !== undefined && (
              <div>
                <p className="text-xs text-gray-400">{t("check_drawer.meta_regex")}</p>
                <p className={`text-sm font-medium ${meta.keyword_regex_matched ? "text-green-600" : "text-red-500"}`}>
                  {meta.keyword_regex_matched ? t("check_drawer.meta_regex_matched") : t("check_drawer.meta_regex_no_match")}
                </p>
              </div>
            )}
            {meta.days_remaining !== undefined && (
              <div>
                <p className="text-xs text-gray-400">{t("check_drawer.meta_days_remaining")}</p>
                <p className={`text-sm font-medium ${
                  meta.days_remaining <= 7 ? "text-red-500"
                  : meta.days_remaining <= 30 ? "text-yellow-600"
                  : "text-green-600"
                }`}>
                  {meta.days_remaining > 0 ? `${meta.days_remaining}d` : t("check_drawer.meta_expired")}
                </p>
              </div>
            )}
            {meta.expires_at && (
              <div>
                <p className="text-xs text-gray-400">{t("check_drawer.meta_expires")}</p>
                <p className="text-sm text-gray-700">
                  {new Date(meta.expires_at).toLocaleDateString()}
                </p>
              </div>
            )}
            {meta.registrar && (
              <div>
                <p className="text-xs text-gray-400">{t("check_drawer.meta_registrar")}</p>
                <p className="max-w-xs truncate text-sm text-gray-700">{meta.registrar}</p>
              </div>
            )}
            {meta.host && !meta.http_status && (
              <div>
                <p className="text-xs text-gray-400">{t("check_drawer.meta_host")}</p>
                <p className="font-mono text-sm text-gray-700">{meta.host}</p>
              </div>
            )}
            {meta.subject_cn && (
              <div>
                <p className="text-xs text-gray-400">{t("check_drawer.meta_cert_cn")}</p>
                <p className="font-mono text-sm text-gray-700">{meta.subject_cn}</p>
              </div>
            )}
            {meta.issuer_cn && (
              <div>
                <p className="text-xs text-gray-400">{t("check_drawer.meta_issuer")}</p>
                <p className="font-mono text-sm text-gray-700">{meta.issuer_cn}</p>
              </div>
            )}
            {meta.san_names && meta.san_names.length > 0 && (
              <div>
                <p className="text-xs text-gray-400">{t("check_drawer.meta_san")}</p>
                <p className="text-sm text-gray-700 max-w-sm truncate" title={meta.san_names.join(", ")}>
                  {meta.san_names.join(", ")}
                </p>
              </div>
            )}
            {meta.is_self_signed && (
              <div>
                <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                  {t("check_drawer.meta_self_signed")}
                </span>
              </div>
            )}
            {meta.title && (
              <div>
                <p className="text-xs text-gray-400">{t("check_drawer.meta_title")}</p>
                <p className="text-sm text-gray-700">{meta.title}</p>
              </div>
            )}
            {meta.final_url && (
              <div>
                <p className="text-xs text-gray-400">{t("check_drawer.meta_final_url")}</p>
                <p className="max-w-md truncate font-mono text-sm text-gray-700">
                  {meta.final_url}
                </p>
              </div>
            )}
          </div>
        )}

        {tabs.length > 0 && (
          <div className="flex gap-1 border-b border-gray-200 px-6 shrink-0">
            {tabs.map((tab_item) => (
              <button
                key={tab_item.id}
                type="button"
                onClick={() => setTab(tab_item.id)}
                className={[
                  "-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors",
                  tab === tab_item.id
                    ? "border-teal-600 text-teal-600"
                    : "border-transparent text-gray-500 hover:text-gray-700",
                ].join(" ")}
              >
                {tab_item.label}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {tabs.length === 0 && (meta.screenshot_url !== undefined || meta.har_url !== undefined) && (
            <p className="text-sm text-gray-400">{t("check_drawer.no_artifacts")}</p>
          )}

          {tab === "steps" && isTransaction && (
            <div className="flex-1 overflow-y-auto p-6">
              <div className="mb-4 flex items-center gap-4 text-sm">
                <span className="text-gray-500">
                  {t("check_drawer.steps_completed", { done: meta.steps_completed, total: meta.total_steps })}
                </span>
                {meta.failed_step_index != null && (
                  <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                    {t("check_drawer.failed_at_step", { num: meta.failed_step_index + 1 })}
                  </span>
                )}
                {meta.failed_step_index == null && (
                  <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                    {t("check_drawer.all_steps_passed")}
                  </span>
                )}
              </div>

              <div className="space-y-2">
                {meta.step_results.map((s) => (
                  <div
                    key={s.index}
                    className={[
                      "rounded-lg border p-3",
                      s.status === "pass"
                        ? "border-green-200 bg-green-50"
                        : "border-red-200 bg-red-50",
                    ].join(" ")}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span
                          className={[
                            "flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold text-white",
                            s.status === "pass" ? "bg-green-500" : "bg-red-500",
                          ].join(" ")}
                        >
                          {s.status === "pass" ? "✓" : "✗"}
                        </span>
                        <span className="text-xs font-medium text-gray-500">
                          {t("check_drawer.step_label", { num: s.index + 1 })}
                        </span>
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-700">
                          {s.action}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400">{s.duration_ms}ms</span>
                    </div>
                    {s.error && (
                      <p className="mt-1.5 text-xs text-red-700">{s.error}</p>
                    )}
                    {s.screenshot_key && (
                      <p className="mt-1 text-xs text-teal-600">{t("check_drawer.screenshot_captured")}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "screenshot" && meta.screenshot_url && (
            <img
              src={meta.screenshot_url}
              alt={t("check_drawer.img_alt")}
              className="w-full rounded border border-gray-200 shadow-sm"
            />
          )}

          {tab === "har" && meta.har_url && (
            <>
              {harLoading && <p className="text-xs text-gray-400">{t("check_drawer.loading_har")}</p>}
              {harError && <p className="text-xs text-red-500">{harError}</p>}
              {har && <HarViewer har={har} />}
            </>
          )}
        </div>
      </DialogPanel>
    </Dialog>
  );
}

export { CheckDetailSummary };
export default PlaywrightCheckDrawer;
